import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Button, Divider, Form, Header, Icon, Image, Label, Segment } from "semantic-ui-react";
import { useMediaQuery } from "react-responsive";
import ModalWrapper from "../../../../app/common/modals/modalWrapper";
import { closeModal } from "../../../../app/common/modals/modalSlice";
import { ensureHttpsImageUrl } from "../../../../app/common/util/util";
import { functionStartPropertyIntelligence } from "../../../../app/firestore/functionsService";
import {
  BuyStrategyCreditsSection,
  FundingSourceCard,
  MissingAddressForm,
  TransActionerProLogo,
  defaultFundingSource,
  hasCompleteAddress,
} from "../strategyComponents/StrategyCreditConfirmModal";
import { NAVY, BLUE, LIGHT_BLUE_BG, GREY_TEXT, PI_PROPERTY_TYPE_OPTIONS, defaultPropertyInputs, prefilledFields } from "./piModel";

const FEATURE_PILLS = [
  { icon: "home", label: "Property Facts" },
  { icon: "file alternate outline", label: "Permits" },
  { icon: "building outline", label: "HOA & Metro" },
  { icon: "warning sign", label: "Property Risks" },
  { icon: "shield alternate", label: "Insurance" },
  { icon: "map outline", label: "Neighborhood" },
];

const NUMBER_FIELDS = [
  ["yearBuilt", "Year Built", "e.g. 1998"],
  ["totalBuildingSqft", "Total Building Sq Ft", "incl. unfinished"],
  ["finishedSqft", "Living / Finished Sq Ft", "all finished"],
  ["aboveGradeSqft", "Above-Grade Finished Sq Ft", ""],
  ["basementSqft", "Basement Sq Ft (total)", ""],
  ["basementFinishedSqft", "Finished Basement Sq Ft", ""],
  ["bedrooms", "Bedrooms", ""],
  ["bathrooms", "Bathrooms", "e.g. 2.5"],
  ["garageSpaces", "Garage Spaces", ""],
  ["lotSizeSqft", "Lot Size (sq ft)", ""],
];

const NOTES_PLACEHOLDER =
  "Examples: Backyard slopes toward the house; investigate drainage and erosion. Square footage may be overstated. Two HOAs and unusually high property taxes. Concern about road noise or past foundation repairs.";

function Pills() {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8 }}>
      {FEATURE_PILLS.map((pill) => (
        <div
          key={pill.label}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, background: LIGHT_BLUE_BG, color: NAVY, borderRadius: 999, padding: "6px 14px", fontWeight: 600, fontSize: "0.85em" }}
        >
          <Icon name={pill.icon} color="blue" style={{ margin: 0 }} />
          {pill.label}
        </div>
      ))}
    </div>
  );
}

/**
 * Intake + (when needed) credit selection for Property Intelligence. One
 * click unlocks the property through the existing Strategy Credit flow (if
 * it isn't already unlocked) and starts the one-time research job; the
 * server makes both steps idempotent.
 */
export default function PropertyIntelligenceUnlockModal({ transaction, entitlement, onStarted }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const [entitlementOverride, setEntitlementOverride] = useState(null);
  const activeEntitlement = entitlementOverride || entitlement;
  const isUnlocked = Boolean(activeEntitlement?.isPropertyUnlocked);
  const [fundingSourceType, setFundingSourceType] = useState(isUnlocked ? null : defaultFundingSource(entitlement));
  const [address, setAddress] = useState(transaction?.address || null);
  const [inputs, setInputs] = useState(() => defaultPropertyInputs(transaction));
  const [prefilled] = useState(() => prefilledFields(transaction));
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const addressReady = hasCompleteAddress(address);

  const { freeComped = 0, paid = 0 } = activeEntitlement?.personalCredits || {};
  const manager = activeEntitlement?.managerSponsoredCredits;

  const setField = (key) => (e, { value }) => setInputs((prev) => ({ ...prev, [key]: value }));
  const fieldLabel = (key, text) => (
    <label>
      {text}
      {prefilled.has(key) && inputs[key] === defaultPropertyInputs(transaction)[key] && (
        <Label size="mini" basic color="blue" style={{ marginLeft: 6 }}>
          From transaction/MLS
        </Label>
      )}
    </label>
  );

  function handleClose() {
    dispatch(closeModal({ modalType: "PropertyIntelligenceUnlockModal" }));
  }

  async function handleStart() {
    if (!isUnlocked && !fundingSourceType) return;
    setSubmitting(true);
    try {
      const result = await functionStartPropertyIntelligence({
        transactionId: transaction.id,
        fundingSourceType: isUnlocked ? undefined : fundingSourceType,
        agentNotes: notes,
        propertyInputs: inputs,
      });
      if (result.outcome === "existing") toast.info("Research already exists for this property - opening it.");
      handleClose();
      if (onStarted) onStarted(result);
    } catch (error) {
      toast.error(error?.message || "Unable to start Property Intelligence research right now.");
      setSubmitting(false);
    }
  }

  return (
    <ModalWrapper size="small">
      <Segment style={{ padding: isMobile ? "1.25rem" : "1.75rem 2rem 2rem" }}>
        <TransActionerProLogo />
        <Header as="h1" style={{ color: NAVY, fontWeight: 800, fontSize: isMobile ? "1.5em" : "1.9em", textAlign: "center", margin: "0 0 6px" }}>
          Unlock Property Intelligence
        </Header>
        <p style={{ color: GREY_TEXT, textAlign: "center", fontSize: "0.95em", margin: "0 auto 14px", maxWidth: 520 }}>
          Research this property&apos;s history, permits, HOA and metro districts, structural and environmental
          considerations, insurance, and neighborhood information.
        </p>
        <Pills />
        <Divider hidden />

        {!addressReady ? (
          <>
            <p style={{ color: GREY_TEXT, textAlign: "center" }}>
              <Icon name="map outline" /> This property doesn&apos;t have an address yet. Enter one below to research it.
            </p>
            <MissingAddressForm transactionId={transaction.id} initialAddress={address} onSaved={setAddress} />
            <div className="clear" />
            <Divider className="medium top margin" />
            <Button onClick={handleClose} type="button" floated={isMobile ? null : "right"} content="Cancel" />
            <div className="clear" />
          </>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 16, background: "#f4f7fa", border: "1px solid #e7ebf0", borderRadius: 10, padding: 12 }}>
              <Image
                src={transaction?.pic ? ensureHttpsImageUrl(transaction.pic) : "/assets/placeholder-house.png"}
                style={{ width: 90, height: 70, objectFit: "cover", borderRadius: 8, flexShrink: 0 }}
              />
              <div>
                <div style={{ fontWeight: 700, color: NAVY, fontSize: "1.05em" }}>
                  {address.street}
                  {address.unit ? ` ${address.unit}` : ""}
                </div>
                <div style={{ color: "#5a6472" }}>
                  {address.city}, {address.state} {address.zipcode || ""}
                </div>
                {inputs.propertyType && <div style={{ color: GREY_TEXT, fontSize: "0.85em", marginTop: 2 }}>{inputs.propertyType}</div>}
              </div>
            </div>

            <Header as="h4" style={{ color: NAVY, margin: "18px 0 4px" }}>
              Property information
            </Header>
            <p style={{ color: GREY_TEXT, fontSize: "0.85em", margin: "0 0 8px" }}>
              Optional. Prefilled from the transaction where available - correct anything you know. Your entries are
              labeled as agent-provided in the report and checked against public records, never treated as verified.
            </p>
            <Form size="small">
              <Form.Select
                label="Property Type"
                placeholder="Select..."
                options={PI_PROPERTY_TYPE_OPTIONS}
                value={PI_PROPERTY_TYPE_OPTIONS.some((o) => o.value === inputs.propertyType) ? inputs.propertyType : null}
                text={inputs.propertyType && !PI_PROPERTY_TYPE_OPTIONS.some((o) => o.value === inputs.propertyType) ? inputs.propertyType : undefined}
                onChange={setField("propertyType")}
              />
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr", gap: "0 12px" }}>
                {NUMBER_FIELDS.map(([key, text, placeholder]) => (
                  <Form.Input key={key} label={fieldLabel(key, text)} placeholder={placeholder} value={inputs[key]} onChange={setField(key)} />
                ))}
              </div>
              <Form.Input
                label="Other relevant characteristics"
                placeholder="e.g. solar panels (owned/leased), well and septic, walkout basement, 2019 roof"
                value={inputs.otherCharacteristics}
                onChange={setField("otherCharacteristics")}
              />

              <div style={{ background: LIGHT_BLUE_BG, border: `1px solid ${BLUE}33`, borderRadius: 10, padding: "14px 16px", marginTop: 14 }}>
                <Form.TextArea
                  label={
                    <label style={{ color: NAVY, fontWeight: 700, fontSize: "1.02em" }}>
                      <Icon name="search" color="blue" />
                      Anything important we should investigate about this property? (Optional)
                    </label>
                  }
                  placeholder={NOTES_PLACEHOLDER}
                  rows={4}
                  value={notes}
                  maxLength={5000}
                  onChange={(e, { value }) => setNotes(value)}
                />
                <p style={{ color: GREY_TEXT, fontSize: "0.8em", margin: 0 }}>
                  Your notes direct the research and each concern is answered in the report&apos;s Agent-Requested
                  Research section. They&apos;re treated as questions to investigate, not as established facts.
                </p>
              </div>
            </Form>

            {!isUnlocked && (
              <>
                <Header as="h4" style={{ color: NAVY, margin: "20px 0 10px" }}>
                  Choose a credit to use:
                </Header>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <FundingSourceCard icon="gift" title="My Free / Comped Credit" count={freeComped} description="Use one of your complimentary credits." selected={fundingSourceType === "free_comped"} disabled={freeComped <= 0} onSelect={() => setFundingSourceType("free_comped")} />
                  <FundingSourceCard icon="credit card outline" title="My Paid Credit" count={paid} description="Use one of your purchased credits." selected={fundingSourceType === "paid"} disabled={paid <= 0} onSelect={() => setFundingSourceType("paid")} />
                  <FundingSourceCard
                    icon="users"
                    title="Manager-Sponsored Credit"
                    count={manager?.available ? manager.balance : 0}
                    description={manager?.available ? `Provided by ${manager.managerName || "your manager"}.` : "Provided by your manager."}
                    selected={fundingSourceType === "manager_pool"}
                    disabled={!manager?.available || manager.balance <= 0}
                    onSelect={() => setFundingSourceType("manager_pool")}
                  />
                </div>
                <Divider hidden />
                <BuyStrategyCreditsSection
                  isMobile={isMobile}
                  transactionId={transaction.id}
                  onCreditsFulfilled={(data) => {
                    setEntitlementOverride(data);
                    setFundingSourceType("paid");
                  }}
                />
              </>
            )}

            <p style={{ color: NAVY, fontWeight: 600, textAlign: "center", fontSize: "0.92em", margin: "18px 0 0" }}>
              <Icon name="info circle" color="blue" />
              Property Intelligence research is generated once for this property. Your completed report, edits, PDFs,
              and client summaries remain available without using another research credit.
            </p>

            <Divider className="medium top margin" />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <Button basic disabled={submitting} onClick={handleClose} type="button" content="Cancel" className={isMobile ? "fluid" : null} />
              <Button
                primary
                size="large"
                fluid={isMobile}
                loading={submitting}
                disabled={submitting || (!isUnlocked && !fundingSourceType)}
                onClick={handleStart}
                content={isUnlocked ? "Research Property" : "Use 1 Credit & Research Property"}
              />
            </div>
            {isUnlocked && (
              <p style={{ color: GREY_TEXT, fontSize: "0.8em", textAlign: "right", marginTop: 6 }}>
                This property is already unlocked - no additional credit is used.
              </p>
            )}
          </>
        )}
      </Segment>
    </ModalWrapper>
  );
}
